import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getAllUsers, updateUser, deleteUser, createUser } from '../api/users/users';
import { useEffect } from 'react';

// Query key factory
export const usersKeys = {
  all: ['users'],
  lists: () => [...usersKeys.all, 'list'],
  list: (filters) => [...usersKeys.lists(), { filters }],
  details: () => [...usersKeys.all, 'detail'],
  detail: (id) => [...usersKeys.details(), id],
};

// Hook for real-time users with TanStack Query
export const useUsersQuery = () => {
  const queryClient = useQueryClient();

  // Initial query to get users
  const query = useQuery({
    queryKey: usersKeys.lists(),
    queryFn: getAllUsers,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Set up real-time listener
  useEffect(() => {
    const usersRef = collection(db, 'users');
    
    const unsubscribe = onSnapshot(
      usersRef,
      (snapshot) => {
        const usersData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // Sort by name for consistent ordering
        const sortedUsers = usersData.sort((a, b) => {
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        // Update the query cache with real-time data
        queryClient.setQueryData(usersKeys.lists(), sortedUsers);
      },
      (error) => {
        console.error('Error in users real-time listener:', error);
        // Optionally invalidate queries on error
        queryClient.invalidateQueries({ queryKey: usersKeys.all });
      }
    );

    return () => unsubscribe();
  }, [queryClient]);

  return query;
};

// Mutation for updating a user
export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ userId, userData }) => updateUser(userId, userData),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating user:', error);
    },
  });
};

// Mutation for deleting a user
export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userId) => deleteUser(userId),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
    onError: (error) => {
      console.error('Error deleting user:', error);
    },
  });
};

// Mutation for creating a user
export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (userData) => createUser(userData),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating user:', error);
    },
  });
};

// Hook for optimistic updates (optional advanced feature)
export const useOptimisticUserUpdate = () => {
  const queryClient = useQueryClient();
  
  const updateUserOptimistically = (userId, userData) => {
    queryClient.setQueryData(usersKeys.lists(), (oldUsers) => {
      if (!oldUsers) return oldUsers;
      
      return oldUsers.map(user => 
        user.id === userId 
          ? { ...user, ...userData }
          : user
      );
    });
  };
  
  const revertOptimisticUpdate = () => {
    queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
  };
  
  return { updateUserOptimistically, revertOptimisticUpdate };
};
